/**
 * Supplier invoice matching hooks (#3033, T12c3): the line-level API of T12b2 under `/inventory` (the old header match endpoint is
 * not used): facts, document list, document detail with the live review, the read-only preview, and the three journaled writes
 * (create, accept a variance, reject).
 *
 * Contract notes
 *  - The server is the only evaluator. Nothing here, or in the screens built on it, computes a total, a VAT amount, a match status or
 *    a coverage; they are shown as the server answered. The only arithmetic is the unit conversion of what the user typed
 *    (`money.ts`, exact decimals).
 *  - Every read needs `reports.view_financial` AND (`purchase_orders.read` OR `purchase_orders.match`); preview, create and reject
 *    need `purchase_orders.match` AND financial; accept needs `purchase_orders.match_override` as well (and so does rejecting an
 *    accepted invoice). A session without them makes no request and shows or caches nothing: the hooks return `undefined` data for a
 *    session that lacks the grant even when a cached answer exists.
 *  - Query keys carry tenant, user and session family, so a cached answer of another principal, or of an earlier login of the same
 *    user, is never shown to this one.
 *  - Every request is bound to the owner (tenant, user, session family, generation), the permission it needs, the lock state and the
 *    target (purchase order or invoice) it began under. They are re-read from the auth store just before the request is sent and again
 *    when the answer arrives: an answer that outlived any of them is never delivered to the screen, the cache or a notification
 *    (`LateResultError`).
 *  - Writes are idempotent commands. The exact request (route, body, `Idempotency-Key`) is persisted in the owner's bounded journal
 *    (`invoiceJournal.ts`) and read back BEFORE it is sent; a storage failure blocks the dispatch. A definite refusal releases the
 *    operation; an ambiguous failure keeps it, and only an explicit `retry` sends the identical request again. A fresh key is never
 *    minted for an intent whose first attempt may have committed.
 */

import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  InvoiceAccessError,
  LateResultError,
  OwnerChangedError,
  fenceHolds,
  isLocalRefusal,
  ownerSegments,
  sameSession,
  takeFence,
  useInvoiceAccess,
  type FenceRequest,
  type InvoiceFence,
  type InvoiceOutcome,
  type InvoiceOwner,
  type InvoiceRequirement,
} from '@/features/purchase-orders/invoices/invoiceSession';
import {
  beginFlight,
  dropCorruptJournal,
  endFlight,
  getJournalView,
  putOperation,
  readBack,
  recordAttempt,
  refreshJournal,
  removeOperation,
  restoreJournal,
  slotOf,
  useJournalView,
  type JournalOperation,
  type JournalStatus,
  type OperationKind,
} from '@/features/purchase-orders/invoices/invoiceJournal';

// ---------------------------------------------------------------------------
// Wire types (services/staff-api schemas/supplier-invoices.schema.ts and lib/supplier-invoice-http views)
// ---------------------------------------------------------------------------

export type InvoiceDocumentKind = 'invoice' | 'credit_note';
export type InvoiceStatus = 'draft' | 'matched' | 'variance' | 'accepted' | 'rejected';

export interface InvoiceLineBody {
  lineNumber: number;
  purchaseOrderLineId: string | null;
  description: string;
  /** Signed: positive goods, negative credit. */
  quantity: number;
  /** Cents at up to four decimals (a JSON number whose shortest decimal text is the exact typed value). */
  unitPriceNetCents: number;
  discountPercent: number;
  vatPercent: number;
  /** What the document printed; `null` = it did not state one. */
  statedLineNetCents: number | null;
  receiptAllocations: { receiptLineId: string; quantity: number }[];
  creditLinks: { returnLineId: string; quantity: number }[];
}

export interface InvoiceDocumentBody {
  documentKind: InvoiceDocumentKind;
  invoiceNumber: string;
  /** `YYYY-MM-DD`. */
  invoiceDate: string;
  currency: 'EUR';
  shipping: { netCents: number; vatPercent: number | null };
  stated: { netTotalCents: number; vatTotalCents: number; grossTotalCents: number };
  lines: InvoiceLineBody[];
}

export type PreviewInvoiceBody = InvoiceDocumentBody & { proposeAllocations: boolean };
export type CreateInvoiceBody = InvoiceDocumentBody & { previewToken: string };
export interface AcceptInvoiceBody {
  reason: string;
  evidenceHash: string;
  expectedRevision: number;
}
export interface RejectInvoiceBody {
  reason: string;
  expectedRevision: number;
}

export interface LineReason {
  code: string;
  quantity?: number;
}

export interface InvoiceLineResult {
  lineNumber: number;
  purchaseOrderLineId: string | null;
  lineKind: 'goods' | 'shipping' | 'credit';
  quantity: number;
  lineNetCents: number;
  matchStatus: string | null;
  reasons: LineReason[];
  varianceCents: number | null;
  varianceQuantity: number | null;
  price: { comparison: string | null; invoiceUnitScaled: number; poUnitScaled: number | null };
  vat: { comparison: string; invoicePercent: number; recordedPercent: number | null };
  quantities: {
    invoiced: number;
    allocated: number;
    allocationMissing: number;
    availableAccepted: number;
    over: number;
    legacyUnsupported: number;
    creditLinked: number;
  };
}

export interface ArithmeticIssue {
  code: string;
  lineNumber?: number;
  expectedCents: number;
  statedCents: number;
}

export interface InvoiceMatchResult {
  proposedStatus: 'matched' | 'variance';
  lines: InvoiceLineResult[];
  arithmetic: {
    ok: boolean;
    issues: ArithmeticIssue[];
    expected: { netCents: number; vatCents: number; grossCents: number; vatByRate: { vatPercent: number; netCents: number; vatCents: number }[] };
    lineRoundingDifferenceCents: number;
  };
  reasons: { code: string; lineNumber?: number }[];
  unsupportedLegacyQuantity: number;
  vatUnknown: boolean;
  stockEffect: 'none';
}

export interface Violation {
  code: string;
  message: string;
  lineNumber?: number;
}

export type EvaluationView = { ok: true; result: InvoiceMatchResult } | { ok: false; violations: Violation[] };

export interface LineCoverage {
  purchaseOrderLineId: string;
  state: 'nothing_accepted' | 'uncovered' | 'partially_covered' | 'covered' | 'legacy_unsupported';
  receiptBackedAccepted: number;
  legacyUnsupportedQuantity: number;
  allocatedLive: number;
  allocatedSettled: number;
  unallocatedAccepted: number;
  unbilledAccepted: number;
  awaitingQualityDecision: number;
  returnedUnits: number;
  creditedReturnUnits: number;
  creditedReturnUnitsLive: number;
  openCreditUnits: number;
  unlinkedCreditUnits: number;
  unsettledCreditUnits: number;
  netAcceptedForMatching: number;
  lineComplete: boolean;
}

export interface PurchaseOrderCoverage {
  lines: LineCoverage[];
  coverageComplete: boolean;
  fullyMatched: boolean;
  awaitingFurtherDelivery: boolean;
  unsupportedLegacyQuantity: number;
  stockEffect: 'none';
}

export type CoverageView = { ok: true; result: PurchaseOrderCoverage } | { ok: false; violations: Violation[] };

export interface FactsReceiptLine {
  id: string;
  receiptId: string;
  receiptNumber: string | null;
  receivedAt: string | null;
  quantityAccepted: number;
  quantityRejected: number;
  allocatedLive: number;
  allocatedSettled: number;
}

export interface FactsReturnLine {
  id: string;
  returnId: string;
  returnNumber: string | null;
  returnStatus: string;
  quantity: number;
  stockEffect: 'withdraw' | 'none';
  sourceReceiptLineId: string | null;
  creditedLive: number;
  creditedSettled: number;
}

export interface FactsLine {
  id: string;
  lineNumber: number;
  partId: string | null;
  partSku: string | null;
  partName: string | null;
  quantityOrdered: number;
  quantityReceived: number;
  quantityHeld: number;
  quantityRejected: number;
  quantityCancelled: number;
  unitPriceNetCents: number;
  discountPercent: number;
  /** `null` = unknown; never derived from a header rate. */
  recordedVatPercent: number | null;
  coverage: LineCoverage | null;
  receiptLines: FactsReceiptLine[];
  returnLines: FactsReturnLine[];
}

export interface InvoiceFacts {
  purchaseOrder: {
    id: string;
    orderNumber: string | null;
    status: string;
    supplierId: string;
    invoiceMatchLevel: 'none' | 'header' | 'line';
    legacyHeaderEvidence: boolean;
    lineFlowAllowed: boolean;
    lineFlowViolations: Violation[];
    invoiceMatched: boolean;
  };
  lines: FactsLine[];
  documents: { id: string; invoiceNumber: string; documentKind: InvoiceDocumentKind; status: InvoiceStatus; revision: number }[];
  coverage: CoverageView;
  stockEffect: 'none';
}

export interface UserRef {
  id: string;
  /** Empty when the server could not name the user: never replaced by a made-up name. */
  name: string;
}

export interface InvoiceSummary {
  id: string;
  purchaseOrderId: string;
  supplierId: string;
  documentKind: InvoiceDocumentKind;
  invoiceNumber: string;
  invoiceDate: string;
  currency: string;
  netTotalCents: number;
  vatTotalCents: number;
  grossTotalCents: number;
  shipping: { netCents: number; vatPercent: number | null };
  status: InvoiceStatus;
  revision: number;
  createdBy: UserRef;
  createdAt: string;
  updatedAt: string;
  acceptedBy: UserRef | null;
  acceptedAt: string | null;
  acceptReason: string | null;
  lineCount: number;
  storedEvaluation: Record<string, unknown> | null;
  storedDecision: Record<string, unknown> | null;
}

export interface InvoiceDetailLine {
  id: string;
  lineNumber: number;
  purchaseOrderLineId: string | null;
  description: string;
  lineKind: string;
  quantity: number;
  unitPriceNetCents: number;
  discountPercent: number;
  vatPercent: number;
  lineNetCents: number;
  statedLineNetCents: number | null;
  matchStatus: string | null;
  varianceCents: number | null;
  varianceQuantity: number | null;
  receiptAllocations: { id: string; receiptLineId: string; receiptNumber: string | null; quantity: number }[];
  creditLinks: { id: string; returnLineId: string; returnNumber: string | null; quantity: number }[];
}

export type InvoiceDetail = InvoiceSummary & { lines: InvoiceDetailLine[] };

export interface InvoiceEvent {
  id: string;
  revision: number;
  eventType: 'created' | 'accepted' | 'rejected';
  fromStatus: string | null;
  toStatus: string;
  actor: UserRef;
  reason: string | null;
  decision: Record<string, unknown>;
  evidence: Record<string, unknown>;
  operationRef: string;
  effectiveAt: string;
}

export type InvoiceReview = { ok: true; revision: number; evidenceHash: string; result: InvoiceMatchResult } | { ok: false; violations: Violation[] };

export interface InvoiceDetailResponse {
  invoice: InvoiceDetail;
  /** The LIVE evaluation against the facts as they are now; `null` for a rejected invoice. */
  review: InvoiceReview | null;
  events: InvoiceEvent[];
  purchaseOrder: { id: string; orderNumber: string | null; status: string; invoiceMatchLevel: string };
  coverage: CoverageView;
  stockEffect: 'none';
}

export interface InvoiceListResponse {
  purchaseOrderId: string;
  items: InvoiceSummary[];
  pagination: { limit: number; offset: number; total: number; hasMore: boolean };
  coverage: CoverageView;
}

export interface AllocationProposal {
  lineNumber: number;
  purchaseOrderLineId: string;
  requestedQuantity: number;
  allocations: { receiptLineId: string; quantity: number }[];
  shortfall: number;
}

export interface InvoicePreviewResponse {
  purchaseOrderId: string;
  blockers: { reason: string; message: string }[];
  evaluation: EvaluationView;
  coverageBefore: CoverageView;
  coverageAfter: CoverageView | null;
  proposals: AllocationProposal[] | null;
  proposalViolations: { code: string; message: string }[];
  evaluationWithProposal: EvaluationView | null;
  previewToken: string;
  proposalOnly: true;
  stockEffect: 'none';
}

export interface InvoiceWriteOutcome {
  kind: 'supplier-invoice-created' | 'supplier-invoice-accepted' | 'supplier-invoice-rejected';
  replayed: boolean;
  status: InvoiceStatus;
  revision: number;
  eventId: string;
  invoice: InvoiceDetail;
  evaluation: EvaluationView | null;
  coverage: CoverageView;
  stockEffect: 'none';
}

// ---------------------------------------------------------------------------
// Keys and cache
// ---------------------------------------------------------------------------

export const INVOICE_PAGE_SIZE = 20;
const INVENTORY = '/inventory';
const ROOT = ['purchase-orders', 'supplier-invoices'] as const;

export const supplierInvoiceKeys = {
  all: ROOT,
  owner: (owner: InvoiceOwner) => [...ROOT, ...ownerSegments(owner)] as const,
  order: (owner: InvoiceOwner, purchaseOrderId: string) => [...supplierInvoiceKeys.owner(owner), 'order', purchaseOrderId] as const,
  facts: (owner: InvoiceOwner, purchaseOrderId: string) => [...supplierInvoiceKeys.order(owner, purchaseOrderId), 'facts'] as const,
  list: (owner: InvoiceOwner, purchaseOrderId: string) => [...supplierInvoiceKeys.order(owner, purchaseOrderId), 'list'] as const,
  detail: (owner: InvoiceOwner, invoiceId: string) => [...supplierInvoiceKeys.owner(owner), 'detail', invoiceId] as const,
};

const SIGNED_OUT = [...ROOT, 'signed-out'] as const;

/**
 * Drops every supplier invoice query of another owner as soon as the owner changes, and every query of this one when its right to
 * read financial matching data is gone: the cached answer is not merely hidden, it is removed.
 */
export function useInvoiceCacheGuard(): void {
  const client = useQueryClient();
  const access = useInvoiceAccess();
  const own = access.owner ? ownerSegments(access.owner) : null;
  const id = own ? JSON.stringify(own) : 'none';
  const canRead = access.canRead;
  useEffect(() => {
    client.removeQueries({
      queryKey: ROOT,
      predicate: (query) => {
        if (own === null || !canRead) return true;
        const key = query.queryKey;
        return !own.every((segment, index) => key[2 + index] === segment);
      },
    });
    // `own` is described by `id`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id, canRead]);
}

// ---------------------------------------------------------------------------
// Fenced requests
// ---------------------------------------------------------------------------

async function settle<T>(call: () => Promise<T>): Promise<InvoiceOutcome<T>> {
  try {
    return { ok: true, value: await call() };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * One request of the session that offered it. The owner, the permission and the target are checked again from the live store just
 * before the request leaves (a refusal before anything is sent), and again when the answer arrives: an answer that outlived its
 * session, tenant, user, generation, lock, permission or target is carried in a `LateResultError`, never returned.
 */
export async function runOwned<T>(request: FenceRequest, requirement: InvoiceRequirement, call: () => Promise<T>): Promise<T> {
  const fence = takeFence(request, requirement);
  const outcome = await settle(call);
  if (!fenceHolds(fence)) throw new LateResultError(outcome);
  if (!outcome.ok) throw outcome.error;
  return outcome.value;
}

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface ReadState<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  refetch: () => void;
}

/** Facts of the order: PO lines, receipts, returns and the live coverage over ALL documents. */
export function useInvoiceFacts(purchaseOrderId: string): ReadState<InvoiceFacts> {
  const access = useInvoiceAccess();
  const query = useQuery({
    queryKey: access.owner ? supplierInvoiceKeys.facts(access.owner, purchaseOrderId) : SIGNED_OUT,
    queryFn: () =>
      runOwned({ owner: access.owner, epoch: access.epoch, target: purchaseOrderId }, 'read', () =>
        api.get<InvoiceFacts>(`${INVENTORY}/purchase-orders/${enc(purchaseOrderId)}/supplier-invoice-facts`),
      ),
    enabled: access.canRead && purchaseOrderId !== '',
    staleTime: 0,
  });
  return {
    data: access.canRead ? query.data : undefined,
    isLoading: access.canRead && query.isLoading,
    isError: access.canRead && query.isError,
    error: access.canRead ? query.error : null,
    isFetching: access.canRead && query.isFetching,
    refetch: () => void query.refetch(),
  };
}

export interface ListState {
  /** Every loaded page's documents, newest first. */
  items: InvoiceSummary[];
  /** The server's true total over ALL documents of the order. */
  total: number | null;
  /** Live coverage of the first loaded page's snapshot. */
  coverage: CoverageView | null;
  hasMore: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
  isError: boolean;
  error: unknown;
  loadMore: () => void;
  refetch: () => void;
}

export function useInvoiceList(purchaseOrderId: string): ListState {
  const access = useInvoiceAccess();
  const query = useInfiniteQuery({
    queryKey: access.owner ? supplierInvoiceKeys.list(access.owner, purchaseOrderId) : SIGNED_OUT,
    queryFn: ({ pageParam }) =>
      runOwned({ owner: access.owner, epoch: access.epoch, target: purchaseOrderId }, 'read', () =>
        api.get<InvoiceListResponse>(`${INVENTORY}/purchase-orders/${enc(purchaseOrderId)}/supplier-invoices`, {
          params: { limit: INVOICE_PAGE_SIZE, offset: pageParam },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: (page) => {
      const { offset, hasMore } = page.pagination;
      // An empty page never loops.
      return hasMore && page.items.length > 0 ? offset + page.items.length : undefined;
    },
    enabled: access.canRead && purchaseOrderId !== '',
    staleTime: 0,
  });
  const allowed = access.canRead;
  const pages = allowed ? (query.data?.pages ?? []) : [];
  const last = pages[pages.length - 1];
  return {
    items: pages.flatMap((page) => page.items),
    total: last ? last.pagination.total : null,
    coverage: pages[0]?.coverage ?? null,
    hasMore: allowed && query.hasNextPage,
    isLoading: allowed && query.isLoading,
    isFetchingMore: allowed && query.isFetchingNextPage,
    isError: allowed && query.isError,
    error: allowed ? query.error : null,
    loadMore: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}

/** One document: lines, stored status and revision, the audit events and the LIVE review whose `evidenceHash` an acceptance quotes. */
export function useInvoiceDetail(invoiceId: string): ReadState<InvoiceDetailResponse> {
  const access = useInvoiceAccess();
  const query = useQuery({
    queryKey: access.owner ? supplierInvoiceKeys.detail(access.owner, invoiceId) : SIGNED_OUT,
    queryFn: () =>
      runOwned({ owner: access.owner, epoch: access.epoch, target: invoiceId }, 'read', () =>
        api.get<InvoiceDetailResponse>(`${INVENTORY}/supplier-invoices/${enc(invoiceId)}`),
      ),
    enabled: access.canRead && invoiceId !== '',
    staleTime: 0,
  });
  return {
    data: access.canRead ? query.data : undefined,
    isLoading: access.canRead && query.isLoading,
    isError: access.canRead && query.isError,
    error: access.canRead ? query.error : null,
    isFetching: access.canRead && query.isFetching,
    refetch: () => void query.refetch(),
  };
}

// ---------------------------------------------------------------------------
// Preview (read-only POST: writes nothing, no key)
// ---------------------------------------------------------------------------

export interface InvoicePreviewer {
  /**
   * Evaluates `body` against the current facts. Rejects with `InvoiceAccessError` / `OwnerChangedError` before anything is sent and with
   * `LateResultError` when the answer outlived the session, a permission or `targetCurrent()`; the caller still fences the answer
   * against its own canonical inputs.
   */
  preview: (purchaseOrderId: string, body: PreviewInvoiceBody, targetCurrent?: () => boolean) => Promise<InvoicePreviewResponse>;
}

export function useInvoicePreview(): InvoicePreviewer {
  const access = useInvoiceAccess();
  return {
    preview: (purchaseOrderId, body, targetCurrent) =>
      runOwned(
        { owner: access.owner, epoch: access.epoch, target: purchaseOrderId, ...(targetCurrent ? { targetCurrent } : {}) },
        'match',
        () => api.post<InvoicePreviewResponse>(`${INVENTORY}/purchase-orders/${enc(purchaseOrderId)}/supplier-invoices/preview`, body),
      ),
  };
}

// ---------------------------------------------------------------------------
// Journaled writes
// ---------------------------------------------------------------------------

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

/** The server's machine `reason` of a refusal, if it sent one. */
export function errorReason(error: unknown): string | null {
  const { data } = (error ?? {}) as ErrorLike;
  const reason = (typeof data === 'object' && data !== null ? (data as { reason?: unknown }).reason : null) ?? null;
  return typeof reason === 'string' ? reason : null;
}

/** The server's HTTP status of a refusal, if it answered. */
export function errorStatus(error: unknown): number | null {
  const { status } = (error ?? {}) as ErrorLike;
  return typeof status === 'number' ? status : null;
}

/**
 * True only when the server decided (or the client refused before sending): a 4xx that is not an auth/session guard, not a timeout,
 * not a rate limit and not the `concurrent_update` rollback that tells the client to repeat the same key. Everything else (a lost
 * connection, a 5xx, a locked session, an expired token, an unparseable answer) leaves the original write's fate open and keeps the key.
 */
export function isDefiniteRefusal(error: unknown): boolean {
  if (isLocalRefusal(error)) return true;
  const status = errorStatus(error);
  if (status === null || status < 400 || status >= 500) return false;
  if ([401, 403, 408, 423, 425, 429].includes(status)) return false;
  return errorReason(error) !== 'concurrent_update';
}

export type OperationBlock = 'corrupt' | 'lost' | 'storage' | 'conflict' | 'full' | 'busy' | 'missing' | 'invalid';

export type OperationResult =
  | { status: 'success'; result: InvoiceWriteOutcome }
  /** The server decided no. The operation is released; the screen shows why and nothing is retried. */
  | { status: 'refused'; error: unknown }
  /** The outcome is unknown. The exact request stays in the journal; only an explicit retry sends it again. */
  | { status: 'uncertain'; error: unknown }
  /** Nothing was sent. */
  | { status: 'blocked'; reason: OperationBlock }
  /** Nothing may be reported to this screen (the owner, a permission or the target changed); `error` is set for a local refusal. */
  | { status: 'canceled'; error?: unknown };

const blockOf = (status: JournalStatus): OperationBlock =>
  status === 'ok' ? 'missing' : status === 'corrupt' ? 'corrupt' : status === 'lost' ? 'lost' : 'storage';

const requirementOf = (kind: OperationKind): InvoiceRequirement => (kind === 'accept' ? 'override' : 'match');

function invalidateAfterWrite(client: QueryClient, owner: InvoiceOwner): void {
  // Facts, lists and details of this owner: matching changed what every one of them says.
  void client.invalidateQueries({ queryKey: supplierInvoiceKeys.owner(owner) });
  // The order's own page (status, receipts) shows what matching changed.
  void client.invalidateQueries({ queryKey: ['inventory', 'purchaseOrders'] });
}

export interface InvoiceOperations {
  /** Unresolved writes of this order (also one found again after a reload). Empty without the right to read financial data. */
  operations: readonly JournalOperation[];
  /** Slots with a request on its way, also one started by a screen that has since unmounted. */
  inFlight: ReadonlySet<string>;
  /**
   * `unavailable`: the browser keeps nothing, no write can be sent. `corrupt`: a record could not be read, nothing new starts.
   * `lost`: the stored record no longer holds requests this page kept; nothing is sent until they are restored or abandoned.
   */
  journalStatus: JournalStatus;
  /** The page still holds the exact requests (and keys) and can write them back with `restore`. */
  restorable: boolean;
  create: (body: CreateInvoiceBody) => Promise<OperationResult>;
  accept: (invoice: { id: string; invoiceNumber: string }, body: AcceptInvoiceBody) => Promise<OperationResult>;
  /** `accepted`: the invoice was accepted, so the rejection needs the override right as well. */
  reject: (invoice: { id: string; invoiceNumber: string; accepted: boolean }, body: RejectInvoiceBody) => Promise<OperationResult>;
  /** Sends the held request again: the same route, payload and key, only for its own session. */
  retry: (operation: JournalOperation) => Promise<OperationResult>;
  /** Gives the held request up. Only an explicit, confirmed user action may call this. */
  discard: (operation: JournalOperation) => boolean;
  /** The user knowingly discards a damaged journal record (it was never used). */
  discardDamaged: () => boolean;
  /** Writes the exact held requests (same keys) back into storage; sends nothing. False when storage refuses. */
  restore: () => boolean;
}

/**
 * The writes of one purchase order's screen, bound to the owner and to `targetCurrent` (false once the screen shows another order).
 * Nothing is sent on mount.
 */
export function useInvoiceOperations(purchaseOrderId: string, targetCurrent?: () => boolean): InvoiceOperations {
  const client = useQueryClient();
  const access = useInvoiceAccess();
  const view = useJournalView(access.owner);

  const fenceFor = (target: string, requirement: InvoiceRequirement): InvoiceFence =>
    takeFence({ owner: access.owner, epoch: access.epoch, target, ...(targetCurrent ? { targetCurrent } : {}) }, requirement);

  const dispatch = async (fence: InvoiceFence, operation: JournalOperation): Promise<OperationResult> => {
    const { owner } = fence;
    // What is sent is what storage holds NOW, byte for byte; a store that cannot be read back blocks the send.
    const stored = readBack(owner, operation.slot, operation.key);
    if (!stored) return { status: 'blocked', reason: blockOf(getJournalView(owner).status) };
    if (!beginFlight(owner, stored.slot, stored.key)) return { status: 'blocked', reason: 'busy' };
    if (!recordAttempt(owner, stored.slot, stored.key)) {
      endFlight(owner, stored.slot, stored.key);
      return { status: 'blocked', reason: 'storage' };
    }
    const raw = await settle(() =>
      api.post<InvoiceWriteOutcome>(stored.path, stored.body, { headers: { 'Idempotency-Key': stored.key } }),
    );
    // The answer belongs to the operation, whoever is on screen now.
    const current = fenceHolds(fence);
    let result: OperationResult;
    if (raw.ok) {
      removeOperation(owner, stored.slot, stored.key);
      if (sameSession(fence)) invalidateAfterWrite(client, owner);
      result = current ? { status: 'success', result: raw.value } : { status: 'canceled' };
    } else if (isDefiniteRefusal(raw.error)) {
      removeOperation(owner, stored.slot, stored.key);
      result = current ? { status: 'refused', error: raw.error } : { status: 'canceled' };
    } else {
      // Unknown: the operation stays, for its owner.
      result = current ? { status: 'uncertain', error: raw.error } : { status: 'canceled' };
    }
    endFlight(owner, stored.slot, stored.key);
    return result;
  };

  const start = async (
    kind: OperationKind,
    target: string,
    label: string,
    body: unknown,
    requirement: InvoiceRequirement,
  ): Promise<OperationResult> => {
    let fence: InvoiceFence;
    try {
      fence = fenceFor(target, requirement);
    } catch (error) {
      return error instanceof InvoiceAccessError ? { status: 'refused', error } : { status: 'canceled', error };
    }
    const journal = getJournalView(fence.owner);
    if (journal.status !== 'ok') {
      refreshJournal();
      return { status: 'blocked', reason: blockOf(journal.status) };
    }
    const slot = slotOf(kind, target);
    if (journal.inFlight.has(slot)) return { status: 'blocked', reason: 'busy' };
    const key = crypto.randomUUID();
    const put = putOperation(fence.owner, { kind, target, purchaseOrderId, label, key, body });
    if (put === 'conflict') return { status: 'blocked', reason: 'conflict' };
    if (put === 'full') return { status: 'blocked', reason: 'full' };
    if (put === 'corrupt') return { status: 'blocked', reason: 'corrupt' };
    if (put === 'lost') return { status: 'blocked', reason: 'lost' };
    if (put === 'storage_failed') return { status: 'blocked', reason: 'storage' };
    if (put === 'invalid') return { status: 'blocked', reason: 'invalid' };
    const entry = getJournalView(fence.owner).operations.find((candidate) => candidate.slot === slot && candidate.key === key);
    if (!entry) return { status: 'blocked', reason: 'storage' };
    return dispatch(fence, entry);
  };

  const retry = async (operation: JournalOperation): Promise<OperationResult> => {
    let fence: InvoiceFence;
    try {
      fence = fenceFor(operation.target, requirementOf(operation.kind));
    } catch (error) {
      // The earlier outcome stays unknown and stays held.
      return { status: 'canceled', error };
    }
    if (operation.purchaseOrderId !== purchaseOrderId) return { status: 'canceled', error: new OwnerChangedError() };
    const journal = getJournalView(fence.owner);
    if (journal.status !== 'ok') {
      refreshJournal();
      return { status: 'blocked', reason: blockOf(journal.status) };
    }
    if (journal.inFlight.has(operation.slot)) return { status: 'blocked', reason: 'busy' };
    return dispatch(fence, operation);
  };

  const discard = (operation: JournalOperation): boolean => {
    let fence: InvoiceFence;
    try {
      fence = fenceFor(operation.target, 'read');
    } catch {
      return false;
    }
    if (getJournalView(fence.owner).inFlight.has(operation.slot)) return false;
    return removeOperation(fence.owner, operation.slot, operation.key);
  };

  const discardDamaged = (): boolean => {
    try {
      return dropCorruptJournal(fenceFor(purchaseOrderId, 'read').owner);
    } catch {
      return false;
    }
  };

  const restore = (): boolean => {
    try {
      return restoreJournal(fenceFor(purchaseOrderId, 'read').owner);
    } catch {
      return false;
    }
  };

  const visible = access.canRead && view.status !== 'corrupt';
  const operations = useMemo(
    () => (visible ? view.operations.filter((operation) => operation.purchaseOrderId === purchaseOrderId) : []),
    [visible, view.operations, purchaseOrderId],
  );

  return {
    operations,
    inFlight: view.inFlight,
    journalStatus: access.canRead ? view.status : 'ok',
    restorable: access.canRead && view.restorable,
    create: (body) => start('create', purchaseOrderId, body.invoiceNumber, body, 'match'),
    accept: (invoice, body) => start('accept', invoice.id, invoice.invoiceNumber, body, 'override'),
    reject: (invoice, body) => start('reject', invoice.id, invoice.invoiceNumber, body, invoice.accepted ? 'override' : 'match'),
    retry,
    discard,
    discardDamaged,
    restore,
  };
}
